import { Button, Divider, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader } from '@ds';
import { Form } from '@ui';
import { toast } from 'react-toastify';
import { dryRunStatuses, turndownStatuses } from '@lp/features/forms/DeclineCaseForm/config';
import {
  CaseStatus,
  DeclineCaseParams,
  OutcomeType,
  DispositionReasonType,
  DispositionSpecificationType,
  TurndownOutcome,
} from '@/api/apis/case';
import { DeclineServiceBody } from './DeclineServiceBody';
import { DeclineCaseFormParams, declineCaseSchema } from '../../config';
import { useDeclineService } from '@/queries/case';
import { Uuid } from '@/api/types';
import { retrieveUnifiedError } from '@/api/utils';
import { getUserTimezone } from '@/utils/helpers';

const getDefaultValues = (status: CaseStatus) => {
  let declineReason = OutcomeType.Aborted;
  if (dryRunStatuses.includes(status)) declineReason = OutcomeType.DryRun;
  else if (turndownStatuses.includes(status)) declineReason = OutcomeType.Turndown;

  return {
    declineReason,
    dispositionReason: DispositionReasonType.CancelledByTransMedics,
    dispositionSpecification: DispositionSpecificationType.OCSContraindicationOrNotSuitable,
    turndownOutcome: '',
    dispositionComment: '',
    timeZone: '',
    timestamp: '',
  };
};

export const DeclineServiceModal = ({
  onOpenChange,
  caseId,
  status,
}: {
  onOpenChange: () => void;
  caseId: Uuid;
  status: CaseStatus;
}) => {
  const { mutateAsync, isPending } = useDeclineService();

  const handleServiceDecline = async ({ turndownOutcome, ...values }: DeclineCaseFormParams) => {
    try {
      let data = { ...values } as DeclineCaseParams;
      data.timeZone = getUserTimezone();
      data.timestamp = new Date().toISOString();

      if (values.declineReason === OutcomeType.Turndown && turndownOutcome) {
        data = {
          ...data,
          turndownOutcome: turndownOutcome as TurndownOutcome,
        };
      }

      await mutateAsync({ caseId, data });
      onOpenChange();
      toast.success('Case service has been declined');
    } catch (e) {
      const error = retrieveUnifiedError(e);
      toast.error(error.message);
    }
  };

  return (
    <Modal
      isOpen
      onOpenChange={onOpenChange}
      size="3xl"
      classNames={{
        wrapper: 'flex-wrap z-[420]',
        footer: 'flex flex-col gap-4',
        closeButton: 'text-hl',
        backdrop: 'z-[419]',
      }}
    >
      <ModalContent>
        <Form<DeclineCaseFormParams>
          defaultValues={getDefaultValues(status)}
          schema={declineCaseSchema}
          onSubmit={handleServiceDecline}
        >
          <ModalHeader className="flex flex-col gap-2">
            <h3 className="text-hl">Transplant not completed</h3>
            <div className="text-l-r text-base-20">
              The case will be canceled across all system. This action cannot be undone
            </div>
            <Divider />
          </ModalHeader>
          <ModalBody className="py-6">
            <DeclineServiceBody status={status} />
          </ModalBody>
          <ModalFooter>
            <Divider />
            <div className="flex gap-2 justify-end">
              <Button size="md" isDisabled={isPending} variant="outline" onClick={onOpenChange}>
                Discard
              </Button>
              <Button size="md" type="submit" isLoading={isPending} color="danger">
                Update Status
              </Button>
            </div>
          </ModalFooter>
        </Form>
      </ModalContent>
    </Modal>
  );
};
